import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectAuthToken } from "../store/authSlice";
import { API_BASE_URL } from "./api";
import toast from 'react-hot-toast';

export function useJobStream(url, options) {
    const retryCount = options?.retryCount ?? 5;
    const retryDelay = options?.retryDelay ?? 2000;
    const enabled = options?.enabled ?? false;
    const token = useSelector(selectAuthToken);
    const [logs, setLogs] = useState([]);
    const [status, setStatus] = useState("idle");
    const [jobStatus, setJobStatus] = useState(null);

    const retryRef = useRef(0);
    const controllerRef = useRef(null);

    const PROCESSING_TOAST_MESSAGE =
        'File is uploaded and is being processed. Please do not press Back button or refresh the page.';

    const connect = () => {
        setStatus("connecting");
        fetchStream();
    };

    const resetLogs = () => {
        setLogs([]);
        setJobStatus(null);
        setStatus("idle");
        retryRef.current = 0;
        controllerRef.current?.abort();
    };

    const fetchStream = async () => {
        try {
            controllerRef.current = new AbortController();

            const headers = {
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
            };
            const jwt = token?.trim();
            if (jwt) {
                headers["jwttoken"] = jwt
            }

            const response = await fetch(`${API_BASE_URL}${url}`, {
                signal: controllerRef.current.signal,
                cache: "no-store",
                credentials: "include",
                headers,
            });

            if (!response.body) throw new Error("No stream");

            setStatus("streaming");
            retryRef.current = 0;

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    setStatus("completed");
                    break;
                }

                buffer += decoder.decode(value, { stream: true });

                const events = buffer.split("\n\n");
                buffer = events.pop() || "";

                for (let evt of events) {
                    if (!evt.trim()) continue;

                    let eventType = "";
                    let data = "";

                    const lines = evt.split("\n");

                    for (let line of lines) {
                        if (line.startsWith("event:")) {
                            eventType = line.replace("event:", "").trim();
                        }
                        if (line.startsWith("data:")) {
                            data = line.replace("data:", "").trim();
                        }
                    }

                    try {
                        const parsed = JSON.parse(data);

                        if (eventType === "row") {
                            // prevent duplicates
                            setLogs((prev) => {
                                const exists = prev.some(
                                    (p) => p.recordOrdinal === parsed.recordOrdinal
                                );
                                return exists ? prev : [...prev, parsed];
                            });
                        }

                        if (eventType === "job") {
                            setJobStatus(parsed);
                            setStatus("completed");
                            controllerRef.current?.abort();
                        }
                    } catch (err) {
                        console.error("Parse error", data);
                    }
                }
            }
        } catch (err) {
            retry();
        }
    };

    const retry = () => {
        if (retryRef.current >= retryCount) {
            setStatus("error");
            return;
        }

        retryRef.current += 1;

        setTimeout(() => {
            connect();
        }, retryDelay * retryRef.current);
    };

    useEffect(() => {
        if (!enabled) return;

        connect();

        return () => {
            controllerRef.current?.abort();
        };
    }, [enabled, url, token]);

    useEffect(() => {
        if (status !== "streaming") return;
        toast.success(PROCESSING_TOAST_MESSAGE, { duration: 12000 });
    }, [status]);

    // ✅ Progress calculation (backend-driven)
    // ✅ LIVE progress (while streaming)
    const processedLive = logs.length;

    // totalRecords may come late → fallback to processedLive
    const totalRecords = jobStatus?.totalRecords || processedLive;

    // final processed count
    const processedFinal =
        (jobStatus?.numberOfSuccessfulRecords || 0) +
        (jobStatus?.numberOfFailedRecords || 0);

    // decide which to use
    const processed =
        status === "completed" ? processedFinal : processedLive;

    // percentage
    const percentage = totalRecords
        ? Math.round((processed / totalRecords) * 100)
        : 0;

    const progress = {
        totalRecords,
        processed,
        success: jobStatus?.numberOfSuccessfulRecords || 0,
        failed: jobStatus?.numberOfFailedRecords || 0,
        percentage,
    };

    return {
        logs,
        status,
        jobStatus,
        progress,
        resetLogs,
    };
}